import { useDisclosure, useToast } from '@chakra-ui/react';
import { type ReactNode, type RefObject, useState } from 'react';

import { useUserTrialDetails } from '@revelio/auth';
import {
  FeatureFlag,
  type MetricValues,
  type SecondaryDimensionValues,
} from '@revelio/core';
import type { UploadFileResult } from '@revelio/data-access';
import type { ReplotChartNames } from '@revelio/replots';

import { useUnleashFlag } from '../../../../../shared/unleash-flags/use-unleash-flag';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { PptPreviewModal } from '../../Modal/PptPreviewModal';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import { useSharedReportData } from '../../SharedReport/SharedReportDataContext';
import { useSafeChart } from '../ChartProvider';
import { PlotLinkModal } from '../PlotLinkModal';
import { getFileName } from '../utils/getFileName';
import {
  ChartActionMenuDropdown,
  type MenuOptions,
} from './ChartActionMenuDropdown';
import type { ShareState } from './ChartMenuContext';
import { DownloadPlotPNG } from './DownloadPlotPng';
import { onDownloadData } from './data-download/download-plot-data';
import {
  exportSingleChartToPpt,
  showExportToast,
  showUploadToast,
} from './data-download/ppt-export/ppt-exporter';
import { uploadFileToStorage } from './data-upload/upload-file';
import { ExpandModal } from './ExpandModal';

const V2_SHARE_COMPONENT_NAMES: ReadonlySet<string> = new Set([
  'AreaChart',
  'BarChart',
  'BoxPlot',
  'GaugeChart',
  'GroupedBarChart',
  'LineChart',
  'SankeyChart',
  'SingleMetric',
  'StackedBarChartHorizontal',
  'TreeMap',
  'VolumeChart',
]);

export type ChartActionMenuProps = {
  menu?: MenuOptions[];
  title: string;
  dataType?: string;
  data?: unknown;
  shareState?: ShareState;
  plotChartType?: ChartType;
  primaryFilterId?: PrimaryFilterId;
  secondaryDimension?: SecondaryDimensionValues;
  metric?: MetricValues;
  pageName: string;
  slim?: boolean;
  printRef?: RefObject<HTMLDivElement>;
  expandModalContent?: ReactNode;
};

export const ChartActionMenu = ({
  menu = ['download-data'],
  title,
  dataType,
  data,
  shareState,
  plotChartType,
  primaryFilterId,
  secondaryDimension,
  metric,
  pageName,
  slim,
  printRef,
  expandModalContent,
}: ChartActionMenuProps) => {
  const toast = useToast();
  const { isTrialUser } = useUserTrialDetails<boolean>();
  const isSharedPlotsEnabled = useUnleashFlag(FeatureFlag.SharedPlots);

  // Enable share if EITHER v2 capture is present (wrapped Replots chart
  // wrote into `getAllChartData` and is in the allow-list) OR legacy
  // `shareState` is populated. v2 capture is flag-gated so v2-only charts
  // don't fall through to the untested legacy path when the flag is off.
  const { item } = useSafeChart();
  const sharedReportData = useSharedReportData();
  const v2CapturedComponent = item?.id
    ? sharedReportData?.getAllChartData()[item.id]?.component
    : undefined;
  const hasV2Capture =
    isSharedPlotsEnabled &&
    !!v2CapturedComponent &&
    V2_SHARE_COMPONENT_NAMES.has(v2CapturedComponent);
  const hasLegacyCapture =
    !!shareState?.chartType && !!shareState?.chartProps && !!shareState?.data;

  let notSharableReason: string | null = null;
  if (isTrialUser) {
    notSharableReason = 'Available on paid plans';
  } else if (!hasV2Capture && !hasLegacyCapture) {
    notSharableReason = 'Sharing is not available for this plot';
  }

  const defaultFileName = getFileName({
    primaryFilterId,
    pageName,
    chartTitle: title,
  });

  const handleDownloadData = () => {
    onDownloadData({
      title,
      dataType,
      data,
      primaryFilterId,
      secondaryDimension,
      metric,
      defaultFileName,
    });
  };

  const {
    isOpen: isExpandModalOpen,
    onOpen: openExpandModal,
    onClose: closeExpandModal,
  } = useDisclosure();

  const {
    isOpen: isSharePlotModalOpen,
    onOpen: openSharePlotModal,
    onClose: closeSharePlotModal,
  } = useDisclosure();

  const {
    isOpen: isDownloadPNGModalOpen,
    onOpen: openDownloadPNGModal,
    onClose: closeDownloadPNGModal,
  } = useDisclosure();

  const handleDownloadPpt = async () => {
    try {
      const exportPromise = exportSingleChartToPpt({
        title,
        defaultFileName,
        chartType: shareState?.chartType as ReplotChartNames | undefined,
        plotChartType,
        selectedSegments: shareState?.chartProps?.selectedSegments as
          | { name: string }[]
          | undefined,
        data,
        dataType,
      }).getFile();

      showExportToast(toast, exportPromise);
    } catch (e) {
      console.error('Error exporting PPT:', e);
    }
  };

  const handleEditPpt = async () => {
    try {
      const uploadPromise = (async (): Promise<
        UploadFileResult | undefined
      > => {
        const result = await exportSingleChartToPpt({
          title,
          defaultFileName,
          chartType: shareState?.chartType as ReplotChartNames | undefined,
          plotChartType,
          selectedSegments: shareState?.chartProps?.selectedSegments as
            | { name: string }[]
            | undefined,
          data,
          dataType,
        }).getBlob();
        if (!result) return;
        return await uploadFileToStorage(result);
      })();

      showUploadToast(toast, uploadPromise);

      const uploadResult = await uploadPromise;
      if (uploadResult) {
        setPreviewUrl(uploadResult.previewUrl);
        setDownloadUrl(uploadResult.downloadUrl);
        setEditUrl(uploadResult.editUrl);
        openPreview();
      }
    } catch (e) {
      console.error('Error previewing PPT:', e);
    }
  };

  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
  const [editUrl, setEditUrl] = useState<string | undefined>(undefined);
  const {
    isOpen: isPreviewOpen,
    onOpen: openPreview,
    onClose: closePreview,
  } = useDisclosure();

  return (
    <>
      <ChartActionMenuDropdown
        menu={menu}
        slim={slim}
        notSharableReason={notSharableReason}
        downloadDataDisabled={isTrialUser}
        downloadPNGDisabled={isTrialUser}
        downloadPPTDisabled={isTrialUser}
        previewPPTDisabled={isTrialUser}
        onExpandPlotClick={openExpandModal}
        onSharePlotClick={openSharePlotModal}
        onDownloadDataClick={handleDownloadData}
        onDownloadPNGClick={openDownloadPNGModal}
        onDownloadPPTClick={handleDownloadPpt}
        onPreviewPPTClick={handleEditPpt}
      />
      <ExpandModal
        isOpen={isExpandModalOpen}
        onClose={closeExpandModal}
        title={title}
      >
        {expandModalContent}
      </ExpandModal>

      <PlotLinkModal
        plotLinkModalState={{
          isPlotLinkOpen: isSharePlotModalOpen,
          onPlotLinkOpen: openSharePlotModal,
          onPlotLinkClose: closeSharePlotModal,
        }}
        cardConfig={{ header: title }}
        typeAndProps={{
          chartType: shareState?.chartType,
          chartProps: shareState?.chartProps || {},
        }}
        data={shareState?.data}
        useV2SharePath={hasV2Capture}
      />

      {printRef && (
        <DownloadPlotPNG
          isOpen={isDownloadPNGModalOpen}
          onClose={closeDownloadPNGModal}
          title={title}
          printRef={printRef}
          defaultFileName={defaultFileName}
        />
      )}

      <PptPreviewModal
        isOpen={isPreviewOpen}
        onClose={closePreview}
        title={defaultFileName || title}
        previewUrl={previewUrl}
        downloadUrl={downloadUrl}
        editUrl={editUrl}
      />
    </>
  );
};
